import { FALLBACK_CONFIG } from './fallback-config';
import { pushGcmDefault, createApi, drainPending, SNIPPET_VERSION } from './api';
import { unblock } from './unblock';
import { readDecision, emptyConsent } from './storage';
import { pushGcmUpdate, pushDataLayerEvent } from './gcm';
import { fetchConfig } from './network';
import type { Config } from './types';

declare global {
  interface Window {
    cookiesie?: ReturnType<typeof createApi>;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

// mvp_b_plan.md §5.2 step 1: synchronous default BEFORE anything else.
// Google tags loaded after this still see denied defaults; the next async
// tick refines via gcmUpdate once a stored decision (or the user) lands.
pushGcmDefault();

(function bootstrap() {
  if (window.cookiesie) return; // double-load guard

  const script = (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-project-id]');

  const projectId = script?.dataset.projectId ?? '';
  const apiBase = (script?.dataset.apiBase ?? 'https://cookies.ie/api/v1').replace(/\/$/, '');
  const configUrl =
    script?.dataset.configUrl ??
    (projectId ? `${apiBase}/config/${projectId}.json` : '');

  const config: Config = {
    ...FALLBACK_CONFIG,
    apiBase,
    configUrl,
  };

  const state = {
    projectId,
    config,
    decision: readDecision(),
    ready: false,
    apiBase,
  };

  const api = createApi(state);
  window.cookiesie = api;

  const internal = api as unknown as {
    _fireReady: () => void;
    _bindFooter: () => void;
    _renderBanner: (startInPrefs?: boolean) => void;
  };

  // Step 3: stored decision wins (until we re-validate against live config).
  if (state.decision && state.decision.pr === config.policyRevision) {
    pushGcmUpdate(state.decision.c);
    unblock(state.decision.c);
    pushDataLayerEvent(state.decision.c);
  } else {
    if (state.decision && state.decision.pr !== config.policyRevision) {
      state.decision = null;
    }
    // Step 4: Sec-GPC honoured as default-reject for analytics + marketing
    // when there's no existing decision and customer hasn't disabled it.
    const gpcOn = navigator.globalPrivacyControl === true;
    if (gpcOn && config.defaults.honourGpc) {
      const c = emptyConsent();
      pushGcmUpdate(c);
      api.rejectAll();
    } else {
      runWhen(config.defaults.showBannerAfter, () => {
        if (!state.decision) internal._renderBanner();
      });
    }
  }

  // Bind footer triggers on the host page.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', internal._bindFooter, {
      once: true,
    });
  } else {
    internal._bindFooter();
  }

  internal._fireReady();

  // Step 5: refresh from live config on next idle tick. If the policy_revision
  // changed under us, drop the stored decision and re-prompt — see §5.2 step 5.
  if (configUrl) {
    afterFirstPaint(async () => {
      const live = await fetchConfig(configUrl);
      if (!live) return;
      const merged: Config = { ...live, apiBase, configUrl };
      const stale =
        state.decision && state.decision.pr !== merged.policyRevision;
      state.config = merged;
      if (stale) {
        state.decision = null;
        if (!navigator.globalPrivacyControl || !merged.defaults.honourGpc) {
          internal._renderBanner();
        }
      }
      // Drain any queued events that failed earlier — banner_text_hash now
      // resolves against the live config so the server will accept them.
      drainPending(state).catch(() => {});
    });
  }
})();

type WhenSpec =
  | 'load'
  | 'interactive'
  | 'idle'
  | { delayMs: number };

function runWhen(spec: WhenSpec, fn: () => void): void {
  if (typeof spec === 'object' && spec) {
    setTimeout(fn, Math.max(0, spec.delayMs));
    return;
  }
  if (spec === 'load') {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', fn, { once: true });
    return;
  }
  if (spec === 'idle') {
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void) => void)
      | undefined;
    if (ric) ric(fn);
    else setTimeout(fn, 200);
    return;
  }
  // 'interactive' (default)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

function afterFirstPaint(fn: () => void): void {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void) => void)
    | undefined;
  if (ric) ric(fn);
  else setTimeout(fn, 0);
}

export { SNIPPET_VERSION };
